import { useEffect, useMemo, useState } from 'react';

import type { AgentExecutionStep } from '../components/AgentMessage';

export type ProcessingStepDef = { id: string; label: string };

export const AGENT_CHAT_PROCESSING_STEPS: ProcessingStepDef[] = [
  { id: 'context', label: 'Analyzing profile context…' },
  { id: 'intent', label: 'Defining task intent and required context…' },
  { id: 'plan', label: 'Planning next action…' },
  { id: 'respond', label: 'Composing response…' },
];

export const JOB_SEARCH_PROCESSING_STEPS: ProcessingStepDef[] = [
  { id: 'profile', label: 'Analyzing your search criteria…' },
  { id: 'vector', label: 'Searching job database…' },
  { id: 'seniority', label: 'Matching experience level…' },
  { id: 'rank', label: 'Ranking by relevance…' },
  { id: 'results', label: 'Preparing results…' },
];

export const RESUME_OPTIMIZE_PROCESSING_STEPS: ProcessingStepDef[] = [
  { id: 'jd', label: 'Parsing job requirements…' },
  { id: 'matrix', label: 'Analyzing experience matrix…' },
  { id: 'keywords', label: 'Mapping ATS keywords…' },
  { id: 'sections', label: 'Rewriting resume sections…' },
  { id: 'score', label: 'Scoring ATS alignment…' },
];

/**
 * Cycles through progressive status labels while a background task is active.
 */
export function useAgentProcessingSteps(
  active: boolean,
  stepDefs: ProcessingStepDef[],
  intervalMs = 950,
): AgentExecutionStep[] {
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!active) {
      setActiveIdx(0);
      return;
    }
    setActiveIdx(0);
    const timer = window.setInterval(() => {
      setActiveIdx((prev) => Math.min(prev + 1, Math.max(stepDefs.length - 1, 0)));
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [active, intervalMs, stepDefs]);

  return useMemo(
    () =>
      stepDefs.map((step, i) => ({
        ...step,
        status:
          i < activeIdx ? ('completed' as const)
          : i === activeIdx ? ('active' as const)
          : ('pending' as const),
      })),
    [stepDefs, activeIdx],
  );
}

/** Prefer the latest LLM [STATUS: ...] label; otherwise use the cycling base step list. */
export function mergeStatusSteps(
  baseSteps: AgentExecutionStep[],
  liveLabels: string[],
): AgentExecutionStep[] {
  if (liveLabels.length > 0) {
    return [{ id: 'live-status', label: liveLabels[liveLabels.length - 1]!, status: 'active' }];
  }
  return baseSteps;
}
